import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Check, ChevronLeft, Star, X } from "lucide-react";
import { TextField } from "react-aria-components";
import type { ManualRatings } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useUpsertManualRating, type ManualMutationError } from "../useManualMutations.js";

/**
 * The completion / rate-on-finish view (ADR-002 Phase 7) — shown when the reader finishes the last step. Everyone sees the
 * "completed" confirmation; only an eligible caller (`canRate` = published manual, not the creator — the server enforces
 * both) gets the 1–5★ + optional comment form, prefilled from their existing rating. On submit it upserts via the shipped
 * `PUT /manuals/:id/rating` and seeds the ratings cache, so the start page's avg/comments update. No rating input lives on
 * the start screen — rating happens HERE (the owner's mockup / legacy rate-on-finish).
 */
export function RateOnFinishPanel({
  session,
  manualId,
  canRate,
  ratings,
  ratingsError = false,
  onBackToSteps,
  onClose,
}: {
  session: AuthSession | null;
  manualId: number;
  canRate: boolean;
  ratings: ManualRatings | null;
  /** The ratings fetch FAILED (not just pending). Then `own` is unknown, so the form is hidden — submitting a fresh (0,"")
   *  would overwrite an existing saved rating/comment (panel). Show a note instead. */
  ratingsError?: boolean;
  onBackToSteps: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const own = ratings?.own ?? null;
  const upsert = useUpsertManualRating(session, manualId);
  const [rating, setRating] = useState(own?.rating ?? 0);
  const [comment, setComment] = useState(own?.comment ?? "");
  const [submitted, setSubmitted] = useState(false);

  const err = upsert.error as ManualMutationError | null;
  const errorMsg = err
    ? err.code === "MANUAL_NOT_RATEABLE"
      ? t("viewer.thisManualCantBeRatedRight")
      : err.code === "RATING_FORBIDDEN"
        ? t("viewer.youCantRateAManualYou")
        : t("viewer.couldntSaveYourRatingPleaseTry")
    : null;

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col items-center justify-center gap-6 p-6 text-center">
      <div className="bg-primary/10 text-primary flex size-14 items-center justify-center rounded-full">
        <Check aria-hidden="true" className="size-7" />
      </div>
      <div>
        <h2 className="text-xl font-semibold">{t("viewer.manualCompleted")}</h2>
        <p className="text-muted-foreground text-sm">{t("viewer.reachedEnd")}</p>
      </div>

      {canRate && ratingsError ? (
        // The fetch failed, so we don't know the caller's existing rating — hide the form so a fresh submit can't erase it.
        <p className="text-muted-foreground text-sm">{t("viewer.ratingLoadFailed")}</p>
      ) : canRate && !submitted ? (
        <section aria-label={t("viewer.rateThisManual")} className="flex w-full flex-col gap-3">
          <p className="text-sm font-medium">{own ? t("viewer.updateYourRating") : t("viewer.rateThisManual")}</p>
          <div role="radiogroup" aria-label={t("viewer.rating")} className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                aria-label={t("viewer.nStars", { count: n })}
                onClick={() => setRating(n)}
                className="focus-visible:ring-ring/50 cursor-pointer rounded-md border-0 bg-transparent p-1 outline-none focus-visible:ring-2"
              >
                <Star aria-hidden="true" className={cn("size-7", n <= rating ? "fill-amber-500 text-amber-500" : "text-muted-foreground")} />
              </button>
            ))}
          </div>
          <TextField aria-label={t("viewer.comment")} value={comment} onChange={setComment} className="w-full">
            <Textarea rows={3} maxLength={2000} placeholder={t("viewer.addACommentOptional")} className="text-sm" />
          </TextField>
          {errorMsg ? <p className="text-destructive text-sm">{errorMsg}</p> : null}
          <Button
            size="lg"
            isDisabled={rating === 0 || upsert.isPending}
            onPress={() => upsert.mutate({ rating, comment: comment.trim() || null }, { onSuccess: () => setSubmitted(true) })}
          >
            {upsert.isPending ? t("viewer.saving") : own ? t("viewer.updateRating") : t("viewer.submitRating")}
          </Button>
        </section>
      ) : submitted ? (
        <p className="text-sm font-medium text-green-600">{t("viewer.thanksForRating")}</p>
      ) : null}

      <div className="flex gap-2">
        <Button variant="outline" onPress={onBackToSteps} className="dark:border-border">
          <ChevronLeft aria-hidden="true" />
          {t("viewer.backToSteps")}
        </Button>
        {/* Closing the viewer is orange too — a deliberate exception carried over from the legacy frontend (owner
            2026-10-22); `accent` always has icon + text. */}
        <Button variant="accent" onPress={onClose}>
          <X aria-hidden="true" />
          {t("viewer.close")}
        </Button>
      </div>
    </div>
  );
}
